import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useForm, Controller, SubmitHandler, FormProvider } from 'react-hook-form';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	Box,
	Typography,
	Grid,
	TextField,
	FormHelperText,
	FormControlLabel,
	RadioGroup,
	Radio,
	Collapse,
} from '@mui/material';
import ContainerBox from 'components/ContainerBox';
import VatSelect from 'components/VatSelect';
import ProductsWithCategorySelect from 'components/ProductsWithCategorySelect';
import DeliveryPeriods from './DeliveryPeriods';
import PriceTextField from './PriceTextField';
import MinimalBookingDuration from 'components/MinimalBookingDuration';

import { DeliveryOptionWithProducts, DeliveryTypes } from 'common/types';
import { getDefaultCurrencyObject } from 'common/utils/currencyUtils';
import { updateDeliveryOption, createDeliveryOption } from 'common/api/frontend';
import { useTranslation } from 'services/localization/useTranslation';
import { DeliveryOptionForm, getDataToSubmit, getDefaultDeliveryPeriod } from './utils';
import { Routes, push } from 'routing';
import errorHandler from 'common/services/errorHandler';
import useScrollToError from 'common/hooks/useScrollToError';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import HandlingTimes from './HandlingTimes';

import {
	isDeliveryType,
	getDefaultHandlingTimes,
	getDefaultOpeningHoursBehaviour,
} from 'common/modules/delivery/utils';
import OpeningHoursBehaviour from './OpeningHoursBehaviour';
import Banner from 'components/Banner';
export interface FormHandler {
	errors: boolean;
	handleSubmit: () => void;
	handleInvalidForm: () => void;
}

interface Props {
	value?: DeliveryOptionWithProducts;
	setLoading?: (loading: boolean) => void;
	setHasChanges?: (hasChanges: boolean) => void;
}

const DeliveryForm = forwardRef((props: Props, ref) => {
	const { value, setLoading, setHasChanges } = props;
	const { t } = useTranslation();
	const classes = useStyles();

	const [formError, setFormError] = useState<string>('');
	const shopId = useSelector(ShopSelectors.activeShopId);
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency) ?? getDefaultCurrencyObject();
	const defaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const activeShopId = useSelector(UserSelectors.userActiveLocationId)!;
	const maxDeliveryOptionOrderIndex = useSelector(ShopSelectors.maxDeliveryOptionOrderIndex);

	const { scrollToFirstError } = useScrollToError({ rootId: 'delivery-form', scrollOffset: -250 });

	const form = useForm<DeliveryOptionForm>({
		defaultValues: {
			type: isDeliveryType(value?.type) ? value!.type : DeliveryTypes.DELIVERY_AND_OPTIONAL_PICKUP,
			name: value?.name?.def ?? '',
			description: value?.description?.def ?? '',
			deliveryPrice: value?.price.delivery ?? 0,
			pickupPrice: value?.price.pickup ?? 0,
			vatPercent: value?.vatPercent ?? 24,
			minimalDurationInSeconds: value?.minimalDurationInSeconds ?? 0,
			locationIds: [activeShopId],
			products: value?.products ?? [],
			deliveryPeriods: value?.deliveryPeriods ?? [getDefaultDeliveryPeriod()],
			hidden: value?.hidden ?? false,
			handlingTimes: value?.handlingTimes ?? getDefaultHandlingTimes(),
			returnInstructions: value?.returnInstructions?.def ?? '',
			openingHoursBehaviour: value?.openingHoursBehaviour ?? getDefaultOpeningHoursBehaviour(),
		},
		mode: 'onChange',
	});

	const {
		register,
		handleSubmit,
		reset,
		setError,
		clearErrors,
		control,
		formState,
		watch,
		getValues,
	} = form;

	const type = watch('type');

	useEffect(() => {
		if (setHasChanges) {
			setHasChanges(formState.isDirty);
		}
	}, [formState.isDirty, setHasChanges]);

	useImperativeHandle(ref, () => ({
		errors: Object.keys(formState.errors).length > 0,
		handleSubmit: handleSubmit(handleSubmitForm, handleInvalidForm),
		handleInvalidForm: handleInvalidForm,
	}));

	if (!shopId) return null;

	const nameRules = {
		required: t('common:validation.cannotBeEmpty', {
			defaultValue: '{{field}} cannot be empty',
			field: t('delivery.name', 'Name'),
		}),
		validate: {
			maxLength: (value: string) =>
				value.length <= 100 ||
				t('common:validation.lengthAtMost', {
					defaultValue: '{{field}} can be at most {{int}} characters',
					field: t('delivery.name', 'Name'),
					int: 100,
				}),
		},
	};

	const returnInstructionRules = {
		required: t('common:validation.cannotBeEmpty', {
			defaultValue: '{{field}} cannot be empty',
			field: t('delivery.customPickupLabel'),
		}),
	};

	const handleInvalidForm = () => {
		scrollToFirstError();
	};

	const handleSubmitForm: SubmitHandler<DeliveryOptionForm> = async (data) => {
		setLoading?.(true);
		try {
			const [deliveryOption, products] = getDataToSubmit(
				{ ...data, locationIds: [activeShopId] },
				shopId,
				maxDeliveryOptionOrderIndex,
				defaultLanguage,
				value,
			);
			if (value) {
				await updateDeliveryOption(deliveryOption, products, value.id, value.products);
				reset(data);
			} else {
				await createDeliveryOption(deliveryOption, products);
				push(Routes.DELIVERY);
			}
		} catch (e) {
			errorHandler.report(e);
			setFormError(e.message);
		} finally {
			setLoading?.(false);
		}
	};

	const renderType = () => (
		<ContainerBox padding="normal">
			<Controller
				control={control}
				name="type"
				render={({ field }) => (
					<RadioGroup {...field}>
						<FormControlLabel
							value={DeliveryTypes.DELIVERY_AND_OPTIONAL_PICKUP}
							control={<Radio />}
							label={t('delivery.typeDeliveryAndOptionalPickup', 'Delivery & optional pickup')}
						/>
						<Box mt={-1}>
							<Collapse in={type === DeliveryTypes.DELIVERY_AND_OPTIONAL_PICKUP}>
								<Typography
									sx={{ ml: 4, maxWidth: 600 }}
									color="secondary"
									variant="body1"
									fontSize="1.2rem"
								>
									{t(
										'delivery.typeDeliveryAndOptionalPickupHelper',
										'Define separate prices for delivery and pickup. The customer can choose whether they want to order pickup, or return the items to your store themselves.',
									)}
								</Typography>
							</Collapse>
						</Box>
						<FormControlLabel
							value={DeliveryTypes.DELIVERY_AND_PICKUP}
							control={<Radio />}
							label={t('delivery.typeDeliveryPickup', 'Delivery & Pickup')}
						/>
						<Box mt={-1}>
							<Collapse in={type === DeliveryTypes.DELIVERY_AND_PICKUP}>
								<Typography
									sx={{ ml: 4, maxWidth: 600 }}
									color="secondary"
									variant="body1"
									fontSize="1.2rem"
								>
									{t(
										'delivery.typeDeliveryPickupHelper',
										'Define a single price for delivery & pickup. The customer always selects both a delivery and pickup time.',
									)}
								</Typography>
							</Collapse>
						</Box>
						<FormControlLabel
							value={DeliveryTypes.DELIVERY_ONLY}
							control={<Radio />}
							label={t('delivery.typeOnlyDelivery', 'Only delivery')}
						/>
						<Box mt={-1}>
							<Collapse in={type === DeliveryTypes.DELIVERY_ONLY}>
								<Typography
									sx={{ ml: 4, maxWidth: 600 }}
									color="secondary"
									variant="body1"
									fontSize="1.2rem"
								>
									{t(
										'delivery.typeDeliveryOnlyHelper',
										'Define a price for one-way delivery. The customer returns the items using separate return instructions.',
									)}
								</Typography>
							</Collapse>
						</Box>
					</RadioGroup>
				)}
			/>
		</ContainerBox>
	);

	const renderAbout = () => {
		const showPickupPrice = type === DeliveryTypes.DELIVERY_AND_OPTIONAL_PICKUP;
		const showReturnInstructions = type === DeliveryTypes.DELIVERY_ONLY;

		return (
			<ContainerBox padding="normal">
				<Typography variant="h5">{t('delivery.about', 'About')}</Typography>
				<Box mt={3} />
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<TextField
							label={t('delivery.name', 'Name')}
							type="text"
							variant="standard"
							fullWidth
							autoFocus={!value}
							placeholder={t('delivery.namePlaceholder', 'E.g. Standard delivery')}
							error={!!formState.errors.name}
							helperText={formState.errors.name?.message}
							{...register('name', nameRules)}
						/>
					</Grid>
					<Grid item xs={12} sm={showPickupPrice ? 4 : 6}>
						<Controller
							name="deliveryPrice"
							render={({ field }) => (
								<PriceTextField
									currency={shopCurrency}
									TextFieldProps={{
										label: showPickupPrice
											? t('delivery.deliveryPriceInclVat', 'Delivery price (incl. VAT)')
											: t('delivery.priceInclVat', 'Price (incl. VAT)'),
										helperText: t('delivery.fixedPricePerBooking2', 'Fixed price per order'),
										placeholder: t('pricing.free'),
										variant: 'standard',
									}}
									{...field}
								/>
							)}
							control={control}
						/>
					</Grid>
					{showPickupPrice && (
						<Grid item xs={12} sm={4}>
							<Controller
								name="pickupPrice"
								render={({ field }) => (
									<PriceTextField
										currency={shopCurrency}
										TextFieldProps={{
											label: t('delivery.pickupPriceInclVat', 'Pickup price (incl. VAT)'),
											helperText: t('delivery.fixedPricePerBooking2', 'Fixed price per order'),
											placeholder: t('pricing.free'),
											variant: 'standard',
										}}
										{...field}
									/>
								)}
								control={control}
							/>
						</Grid>
					)}
					<Grid item xs={12} sm={showPickupPrice ? 4 : 6}>
						<Controller
							name="vatPercent"
							render={() => <VatSelect noMargin={true} />}
							control={control}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							label={t('delivery.description', 'Description')}
							variant="outlined"
							type="text"
							fullWidth
							{...register('description')}
							placeholder={t(
								'delivery.descriptionPlaceholder',
								'E.g. We deliver max. 20km from the store.',
							)}
							multiline
							rows={2}
						/>
					</Grid>
					{showReturnInstructions && (
						<Grid item xs={12}>
							<TextField
								label={t('delivery.customPickupLabel', 'Order return instructions')}
								variant="outlined"
								type="text"
								fullWidth
								{...register('returnInstructions', returnInstructionRules)}
								error={!!formState.errors.returnInstructions}
								placeholder={t('delivery.customPickupPlaceholder', {
									address: '1600 Pennsylvania Avenue, Washington, D.C., USA',
									defaultValue: 'E.g. Please mail these products to address: {{address}}',
								})}
								helperText={formState.errors.returnInstructions?.message}
								multiline
								rows={2}
							/>
						</Grid>
					)}
				</Grid>
			</ContainerBox>
		);
	};

	const renderProducts = () => (
		<ContainerBox>
			<Typography variant="h5">{t('common:products.products', 'Products')}</Typography>
			<Typography variant="body2" color="secondary">
				{t('delivery.productsDescription', 'Select products available for delivery')}
			</Typography>
			<Box mt={4} />
			<Controller
				name="products"
				render={({ field }) => (
					<ProductsWithCategorySelect
						isLiftTicketsFiltered={true}
						TextFieldProps={{
							error: !!formState.errors.products && !!formState.errors.products.message,
						}}
						{...field}
					/>
				)}
				control={control}
			/>
			{!!formState.errors.products && !!formState.errors.products.message && (
				<FormHelperText error data-error="true">
					{formState.errors.products.message}
				</FormHelperText>
			)}
		</ContainerBox>
	);

	const renderSettings = () => (
		<ContainerBox padding="normal">
			<Typography variant="h5">{t('delivery.availability', 'Availability')}</Typography>
			<Typography variant="body2" color="secondary">
				{t('delivery.availabilityDescription', 'Set when delivery is available')}
			</Typography>
			<Box mt={3} />
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="h6" className={classes.subHeader}>
						{t('delivery.minBookingDuration')}
					</Typography>
					<Controller
						name="minimalDurationInSeconds"
						render={({ field }) => (
							<MinimalBookingDuration
								error={formState.errors.minimalDurationInSeconds?.message}
								setError={(e?: string) =>
									setError('minimalDurationInSeconds', { type: 'error', message: e })
								}
								{...field}
							/>
						)}
						control={control}
					/>
				</Grid>
			</Grid>
			<Box mt={4} />
			<Typography variant="h6" className={classes.subHeader}>
				{t('delivery.deliverySlots', 'Delivery slots')}
			</Typography>
			<DeliveryPeriods
				control={control}
				errors={formState.errors}
				clearErrors={clearErrors}
				getValues={getValues}
			/>
			<Box mt={4} />
			<Typography variant="h6" className={classes.subHeader}>
				{t('delivery.startAndEndDays', 'Start and end days')}
			</Typography>
			<Controller
				name="openingHoursBehaviour"
				render={({ field }) => (
					<OpeningHoursBehaviour value={field.value} onChange={field.onChange} type={type} />
				)}
				control={control}
			/>
		</ContainerBox>
	);

	const renderHandlingTimes = () => (
		<ContainerBox padding="normal">
			<Typography variant="h5">{t('delivery.handlingTimes', 'Handling times')}</Typography>
			<Box mt={2} />
			<Controller
				name="handlingTimes"
				render={({ field }) => <HandlingTimes value={field.value} onChange={field.onChange} />}
				control={control}
			/>
		</ContainerBox>
	);

	return (
		<Box id="delivery-form">
			{!!formError && <Banner variant="danger" title={formError} data-error="true" />}
			<FormProvider {...form}>
				{renderType()}
				{renderAbout()}
				{renderProducts()}
				{renderSettings()}
				{renderHandlingTimes()}
			</FormProvider>
		</Box>
	);
});

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		hidden: {
			display: 'none',
		},
		subHeader: {
			fontSize: '1.8rem',
		},
	}),
);

export default DeliveryForm;
